import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import { creditNoteService } from "../services";
import { api } from "../utils/apiService";
import CircularProgress from "@mui/material/CircularProgress";
import PremiumInvoiceViewDialog from "../components/InvoiceViewDialog";
import SelectInvoiceDialog from "../components/credit-note/SelectInvoiceDialog";
import ExportButton from "../components/ExportButton";
import ZatcaQrSection from "../components/sales-invoice/ZatcaQrSection";
import image from "../assets/riyal.png";
import { useTranslation } from "../hooks/useTranslation";
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  TextField,
  InputAdornment,
  Grid,
  Chip,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Menu,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  NoteAdd as CreditNoteIcon,
  Receipt as ReceiptIcon,
  ArrowForward as ArrowForwardIcon,
  Print as PrintIcon,
  Sync as SyncIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

function CreditNote() {
  const { t } = useTranslation();
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({});
  const [sortModel, setSortModel] = useState([
    { field: "createdAt", sort: "desc" },
  ]);

  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [selectInvoiceDialogOpen, setSelectInvoiceDialogOpen] = useState(false);
  const [selectedViewInvoice, setSelectedViewInvoice] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [resubmittingCreditNoteId, setResubmittingCreditNoteId] =
    useState(null);
  const [phase2Enabled, setPhase2Enabled] = useState(false);

  const fetchCreditNotes = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/login");
        return;
      }

      // Build query parameters
      const queryFilters = {
        ...filters,
        ...(searchQuery && { search: searchQuery }),
        ...(sortModel[0] && {
          sortBy: sortModel[0].field,
          sortOrder: sortModel[0].sort,
        }),
      };

      // Use the creditNoteService instead of direct fetch
      const data = await creditNoteService.getCreditNotes(
        queryFilters,
        page + 1,
        rowsPerPage,
      );
      console.log("Raw credit notes data:", data);
      console.log("Credit notes array:", data.data);

      if (data.success) {
        // Log each credit note's fields
        data.data.forEach((note, index) => {
          console.log(`Credit Note ${index + 1}:`, {
            creditNoteNo: note.creditNoteNo,
            invoiceNo: note.invoiceNo,
            refInvoiceNo: note.refInvoiceNo,
            customerName: note.customerName,
            totalAmt: note.totalAmt,
          });
        });

        setCreditNotes(data.data);
        setTotalRows(data.pagination.total);
        if (data.phase2Enabled !== undefined) {
          setPhase2Enabled(data.phase2Enabled);
        } else {
          // Fallback if backend does not include phase2Enabled in response
          const storedTenant = JSON.parse(
            localStorage.getItem("tenant") || "{}",
          );
          const hasZatcaNotes = data.data?.some(
            (note) => note.zatcaStatus || note.zatcaUUID,
          );
          setPhase2Enabled(
            Boolean(
              storedTenant.isPhase2Compliant ||
                storedTenant.settings?.zatcaEnvironmentType ||
                hasZatcaNotes,
            ),
          );
        }
      } else {
        throw new Error(data.message || "Failed to fetch credit notes");
      }
    } catch (error) {
      console.error("Error fetching credit notes:", error);
      // toast.error('Failed to fetch credit notes');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, filters, sortModel, navigate]);

  // Add logging for credit notes state changes
  useEffect(() => {
    console.log("Current credit notes state:", creditNotes);
  }, [creditNotes]);

  useEffect(() => {
    fetchCreditNotes();
  }, [fetchCreditNotes]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handlePrintCreditNote = async (note) => {
    if (!note) return;

    try {
      setLoading(true);
      const invoiceNo =
        note.creditNoteNo || note.invoiceNo || note.id || note._id;
      const type = note.customerType === "B2B" ? "a4" : "thermal";

      const printEndpoint = `/print/creditnote/${invoiceNo}?type=${type}`;

      const response = await api.get(printEndpoint);
      const { html, css } = response.data;

      const printWindow = window.open("", "_blank");
      const printContent = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Credit Note ${invoiceNo}</title>
            <meta charset="utf-8">
            <style>
              ${css}
            </style>
          </head>
          <body>
            ${html}
            <script>
              window.onload = function() {
                window.print();
                window.close();
              }
            </script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      enqueueSnackbar("Print window opened successfully", {
        variant: "success",
      });
    } catch (error) {
      console.error("Print error:", error);
      enqueueSnackbar(
        `Print failed: ${error.response?.data?.message || error.message || "Unknown error"}`,
        { variant: "error" },
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (filterType, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      if (value === "all") {
        delete newFilters[filterType];
      } else {
        newFilters[filterType] = value;
      }
      return newFilters;
    });
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(0);
    handleFilterClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const renderZatcaStatus = (zatcaStatus) => {
    if (!zatcaStatus || zatcaStatus === "") {
      return (
        <Tooltip title={t("Sales.Not_submitted_to_ZATCA")}>
          <RemoveIcon fontSize="small" sx={{ color: "text.disabled" }} />
        </Tooltip>
      );
    }

    if (["CLEARED", "REPORTED", "SUCCESS"].includes(zatcaStatus)) {
      return (
        <Tooltip title={`${t("Sales.Submitted")}: ${zatcaStatus}`}>
          <CheckCircleIcon
            fontSize="small"
            sx={{
              color: "#4caf50",
              "&:hover": { color: "#45a049" },
            }}
          />
        </Tooltip>
      );
    }

    return (
      <Tooltip title={`${t("Sales.Failed")}: ${zatcaStatus}`}>
        <CancelIcon
          fontSize="small"
          sx={{
            color: "#f44336",
            "&:hover": { color: "#d32f2f" },
          }}
        />
      </Tooltip>
    );
  };

  const handleResubmitZatca = async (note) => {
    try {
      const creditNoteNo = note.creditNoteNo || note.invoiceNo || note.id;
      setResubmittingCreditNoteId(creditNoteNo);

      // Support x-environment-type header if configured
      const tenant = JSON.parse(localStorage.getItem("tenant") || "{}");
      const environmentType = tenant?.settings?.zatcaEnvironmentType;

      const headers = {};
      if (environmentType) {
        headers["x-environment-type"] = environmentType;
      }

      const response = await api.get(
        `/creditNotes/${creditNoteNo}/resubmit-zatca`,
        { headers },
      );
      if (response.data.success) {
        enqueueSnackbar("Credit Note resubmitted to ZATCA successfully", {
          variant: "success",
        });
        fetchCreditNotes(); // Refresh the list
      } else {
        enqueueSnackbar(
          response.data.message || "Failed to resubmit to ZATCA",
          { variant: "error" },
        );
      }
    } catch (error) {
      console.error("Error resubmitting to ZATCA:", error);
      enqueueSnackbar(
        error.response?.data?.message ||
          error.message ||
          "Error resubmitting to ZATCA",
        { variant: "error" },
      );
    } finally {
      setResubmittingCreditNoteId(null);
    }
  };

  return (
    <Container
      maxWidth={false}
      sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            {t("CreditNotes.Credit_Notes")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t("CreditNotes.Manage_your_credit_notes")}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <ExportButton
            data={creditNotes.map((note) => ({
              "Credit Note #": note.creditNoteNo,
              Date: new Date(note.invoiceDate).toLocaleDateString(),
              Customer: note.customerName,
              "Ref Invoice No": note.refInvoiceNo,
              "Total Amount": note.totalAmt.toFixed(2),
              "VAT Amount": note.vatAmt.toFixed(2),
              "Grand Total": (note.totalAmt + note.vatAmt).toFixed(2),
              Status: note.status,
              "Customer Type": note.customerType,
            }))}
            filename="credit_notes"
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            size="small"
            onClick={() => setSelectInvoiceDialogOpen(true)}
            sx={{
              borderRadius: "4px",
              textTransform: "none",
              py: 0.5,
              px: 1,
            }}
          >
            {t("CreditNotes.New_Credit_Note")}
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          my: 2,
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder={t("Common.SearchCredits")}
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            maxWidth: 500,
            height: 28,
            "& .MuiOutlinedInput-root": {
              borderRadius: "2px",
              height: 28,
            },
            "& .MuiInputBase-input": {
              py: 0.5,
            },
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          size="small"
          onClick={handleFilterClick}
          sx={{
            borderRadius: "2px",
            textTransform: "none",
            py: 0.25,
            px: 1,
            height: 28,
            ml: 1,
          }}
        >
          {t("Common.Filters")}
        </Button>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: { width: 200 },
        }}
      >
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Status
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {["all", "new", "hold", "posted"].map((status) => (
              <Button
                key={status}
                size="small"
                variant={
                  filters.status === status ||
                  (status === "all" && !filters.status)
                    ? "contained"
                    : "outlined"
                }
                onClick={() => handleFilterChange("status", status)}
                sx={{ textTransform: "capitalize" }}
              >
                {status}
              </Button>
            ))}
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
            Customer Type
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {["all", "B2B", "B2C"].map((type) => (
              <Button
                key={type}
                size="small"
                variant={
                  filters.customerType === type ||
                  (type === "all" && !filters.customerType)
                    ? "contained"
                    : "outlined"
                }
                onClick={() => handleFilterChange("customerType", type)}
                sx={{ textTransform: "none" }}
              >
                {type}
              </Button>
            ))}
          </Box>

          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Button
              size="small"
              onClick={handleClearFilters}
              sx={{ textTransform: "none" }}
            >
              Clear Filters
            </Button>
          </Box>
        </Box>
      </Menu>

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          mb: 0.5,
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderRadius: "4px",
        }}
      >
        <Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      minWidth: "150px",
                    }}
                  >
                    {t("CreditNotes.Credit_Notes")} #
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      minWidth: "120px",
                    }}
                  >
                    {t("Common.Date")}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      minWidth: "150px",
                    }}
                  >
                    {t("Common.Ref_Invoice_No")}
                  </TableCell>
                  <TableCell
                    align="left"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      minWidth: "200px",
                    }}
                  >
                    {t("Common.Customer")}
                  </TableCell>
                  {phase2Enabled && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: "medium",
                        color: "text.secondary",
                        py: 0.5,
                        minWidth: "120px",
                      }}
                    >
                      {t("Sales.ZATCA") || t("CreditNotes.zatcaStatus")}
                    </TableCell>
                  )}
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      minWidth: "120px",
                    }}
                  >
                    {t("Common.Total_Amount")}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      minWidth: "120px",
                    }}
                  >
                    {t("Common.VAT_Amount")}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      minWidth: "120px",
                    }}
                  >
                    {t("Common.Grand_Total")}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: "medium",
                      color: "text.secondary",
                      py: 0.5,
                      width: "80px",
                    }}
                  >
                    {t("Common.Actions")}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={phase2Enabled ? 8 : 7} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : creditNotes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={phase2Enabled ? 8 : 7} align="center">
                      No credit notes found
                    </TableCell>
                  </TableRow>
                ) : (
                  creditNotes.map((note, index) => {
                    console.log(`Rendering credit note ${index + 1}:`, {
                      creditNoteNo: note.creditNoteNo,
                      invoiceNo: note.invoiceNo,
                      refInvoiceNo: note.refInvoiceNo,
                    });
                    return (
                      <TableRow
                        key={note._id}
                        hover
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell align="left" sx={{ py: 0.5 }}>
                          <Typography
                            variant="body2"
                            color="primary"
                            fontWeight="medium"
                          >
                            {note.creditNoteNo || note.invoiceNo || "N/A"}
                          </Typography>
                        </TableCell>
                        <TableCell align="left" sx={{ py: 0.5 }}>
                          {new Date(note.invoiceDate).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "2-digit",
                              day: "2-digit",
                            },
                          )}
                        </TableCell>
                        <TableCell align="left" sx={{ py: 0.5 }}>
                          {note.refInvoiceNo || "-"}
                        </TableCell>
                        <TableCell align="left" sx={{ py: 0.5 }}>
                          {note.customerName || "-"}
                        </TableCell>
                        {phase2Enabled && (
                          <TableCell align="center" sx={{ py: 0.5 }}>
                            {renderZatcaStatus(note.zatcaStatus)}
                          </TableCell>
                        )}{" "}
                        <TableCell align="right" sx={{ py: 0.5 }}>
                          <img src={image} alt="" height={13} width={13} />{" "}
                          {note.subTotal?.toFixed(2) || "0.00"}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5 }}>
                          <img src={image} alt="" height={13} width={13} />{" "}
                          {note.vatAmt?.toFixed(2) || "0.00"}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 0.5 }}>
                          <img src={image} alt="" height={13} width={13} />{" "}
                          {note.totalAmt?.toFixed(2) || "0.00"}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 0.5 }}>
                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            {/* <IconButton
                            size="small"
                            color="primary"
                            sx={{ mx: 0.25, p: 0.5 }}
                            onClick={async () => {
                              try {
                                setViewLoading(true);
                                const token = localStorage.getItem('token');
                                if (!token) {
                                  navigate('/login');
                                  return;
                                }

                                console.log('Fetching credit note:', note.invoiceNo);
                                console.log('Using token:', token);

                                // Use the creditNoteService instead of direct fetch
                                const data = await creditNoteService.getCreditNoteByInvoiceNo(note.invoiceNo);
                                console.log('API Response data:', data);

                                if (data.success) {
                                  console.log('Credit note details:', data.data);
                                  setSelectedViewInvoice(data.data);
                                  setViewDialogOpen(true);
                                } else {
                                  console.error('API Error:', data.message);
                                }
                              } catch (error) {
                                console.error('Error fetching credit note details:', error);
                              } finally {
                                setViewLoading(false);
                              }
                            }}
                          >
                            {viewLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              <VisibilityIcon fontSize="small" />
                            )}
                          </IconButton> */}
                            <IconButton
                              size="small"
                              color="info"
                              sx={{ mx: 0.25, p: 0.5 }}
                              onClick={() => handlePrintCreditNote(note)}
                              title="Print Credit Note"
                            >
                              <PrintIcon fontSize="small" />
                            </IconButton>
                            {phase2Enabled &&
                              !["CLEARED", "REPORTED", "SUCCESS"].includes(
                                note.zatcaStatus,
                              ) && (
                                <Tooltip title={t("Sales.Resubmit_to_ZATCA") || "Resubmit to ZATCA"}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleResubmitZatca(note)}
                                    disabled={
                                      resubmittingCreditNoteId ===
                                      (note.creditNoteNo ||
                                        note.invoiceNo ||
                                        note.id)
                                    }
                                    sx={{
                                      color: "warning.main",
                                      mx: 0.25,
                                      p: 0.5,
                                    }}
                                  >
                                    {resubmittingCreditNoteId ===
                                    (note.creditNoteNo ||
                                      note.invoiceNo ||
                                      note.id) ? (
                                      <CircularProgress
                                        size={20}
                                        color="inherit"
                                      />
                                    ) : (
                                      <SyncIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={totalRows}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: "1px solid rgba(0, 0, 0, 0.12)",
              "& .MuiTablePagination-select": {
                py: 0.5,
              },
              "& .MuiTablePagination-displayedRows": {
                fontSize: "0.875rem",
              },
            }}
          />
        </Box>
      </Paper>

      {/* ZATCA QR Section */}
      {selectedViewInvoice && (
        <Box sx={{ mt: 2 }}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <ZatcaQrSection
              invoice={selectedViewInvoice}
              title="Credit Note QR Code"
            />
          </Paper>
        </Box>
      )}
      {/* Select Invoice Dialog */}
      <SelectInvoiceDialog
        open={selectInvoiceDialogOpen}
        onClose={() => setSelectInvoiceDialogOpen(false)}
        dialogTitle={t("CreditNotes.Select_Invoice_for_Credit_Note")}
        currencyIcon={image}
        onSelectInvoice={(invoice) => {
          navigate(`/credit-notes/new?invoiceId=${invoice._id}`, {
            state: { selectedInvoice: invoice },
          });
        }}
      />
      <PremiumInvoiceViewDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setSelectedViewInvoice(null);
        }}
        invoice={selectedViewInvoice}
        loading={viewLoading}
        title="Credit Note Details"
        currencySymbol="SAR"
        currencyIcon={image}
        onPrint={() => window.print()}
      />
    </Container>
  );
}

export default CreditNote;
